import React, { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail, today } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Card, Btn, Field, Input, Select, Combobox, Empty } from "@/components/UI";
import { toast } from "sonner";
import { ArrowClockwise } from "@phosphor-icons/react";

const emptyLine = { product_id: "", pcs: "", labels_used: "", stickers_used: "", boxes_used: "", stitching_job_id: "" };
const emptyForm = () => ({
  date: today(),
  lines: [{ ...emptyLine }],
  worker_id: "",
  notes: "",
});

export default function PackingPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [products, setProducts] = useState([]);
  const [accessories, setAccessories] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [f, setF] = useState(emptyForm());
  const [editing, setEditing] = useState(null);
  const [ready, setReady] = useState([]);

  const load = () => {
    api.get("/packing").then((r) => setList(r.data));
    api.get("/ready-for-packing").then((r) => setReady(r.data)).catch(() => setReady([]));
  };
  useEffect(() => {
    load();
    api.get("/products").then((r) => setProducts(r.data));
    api.get("/accessories").then((r) => setAccessories(r.data));
    api.get("/workers").then((r) => setWorkers(r.data.filter((w) => w.active !== false)));
  }, []);

  const stockOf = (type) => {
    const a = accessories.find((x) => x.type === type);
    return a ? a.stock_qty : 0;
  };

  const ppbFor = (pid) => (products.find((p) => p.id === pid) || {}).pieces_per_box || 0;

  const updateLine = (idx, patch) => {
    setF((prev) => {
      const next = [...prev.lines];
      const merged = { ...next[idx], ...patch };
      // If pcs changed and boxes_used empty (or auto-tracked), auto-derive from ppb.
      // Use FLOOR so partial/loose pcs don't inflate the full-box count.
      // (Loose pcs are still packed as pcs, they just don't fill a whole box.)
      if ("pcs" in patch) {
        const ppb = ppbFor(merged.product_id);
        if (ppb > 0 && patch.pcs !== "") {
          merged.boxes_used = String(Math.floor(Number(patch.pcs) / ppb));
        }
      }
      // If product changed, reset derived box count
      if ("product_id" in patch) {
        merged.boxes_used = "";
      }
      next[idx] = merged;
      return { ...prev, lines: next };
    });
  };
  const addLine = () => setF((prev) => ({ ...prev, lines: [...prev.lines, { ...emptyLine }] }));
  const removeLine = (idx) => setF((prev) => ({
    ...prev,
    lines: prev.lines.length === 1 ? [{ ...emptyLine }] : prev.lines.filter((_, i) => i !== idx),
  }));

  const totals = useMemo(() => {
    let pcs = 0, boxes = 0, labels = 0, stickers = 0;
    f.lines.forEach((l) => {
      pcs += Number(l.pcs) || 0;
      boxes += Number(l.boxes_used) || 0;
      labels += Number(l.labels_used) || 0;
      stickers += Number(l.stickers_used) || 0;
    });
    return { pcs, boxes, labels, stickers };
  }, [f.lines]);

  const startEdit = (p) => {
    // Editing edits ONE existing packing entry (single-line UI-loaded form).
    setEditing(p.id);
    setF({
      date: p.date,
      lines: [{
        product_id: p.product_id,
        pcs: String(p.pcs || ""),
        labels_used: String(p.labels_used || 0),
        stickers_used: String(p.stickers_used || 0),
        boxes_used: String(p.boxes_used || 0),
      }],
      worker_id: p.worker_id || "",
      notes: p.notes || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const cancelEdit = () => { setEditing(null); setF(emptyForm()); };

  const submit = async (e) => {
    e.preventDefault();
    const clean = f.lines.filter((l) => l.product_id && Number(l.pcs) > 0);
    if (clean.length === 0) return toast.error("Add at least one product line with pieces > 0");
    try {
      if (editing) {
        // Single-line edit path — keep existing PUT endpoint semantics.
        const l = clean[0];
        await api.put(`/packing/${editing}`, {
          date: f.date, product_id: l.product_id,
          pcs: parseInt(l.pcs),
          labels_used: parseInt(l.labels_used || 0),
          stickers_used: parseInt(l.stickers_used || 0),
          boxes_used: parseInt(l.boxes_used || 0),
          worker_id: f.worker_id || null,
          notes: f.notes,
        });
        toast.success("Packing updated · accessory stock re-adjusted");
      } else {
        // Multi-line new packing: POST each line sequentially (small N, sequential keeps
        // accessory-stock deductions deterministic).
        for (const l of clean) {
          await api.post("/packing", {
            date: f.date, product_id: l.product_id,
            pcs: parseInt(l.pcs),
            labels_used: parseInt(l.labels_used || 0),
            stickers_used: parseInt(l.stickers_used || 0),
            boxes_used: parseInt(l.boxes_used || 0),
            worker_id: f.worker_id || null,
            stitching_job_id: l.stitching_job_id || null,
            notes: f.notes,
          });
        }
        toast.success(`Packed ${clean.length} product${clean.length === 1 ? "" : "s"} · ${totals.pcs} pcs total`);
      }
      cancelEdit();
      load();
      api.get("/accessories").then((r) => setAccessories(r.data));
    } catch (err) { toast.error(formatApiErrorDetail(err?.response?.data?.detail)); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete? Accessory stock will be restored.")) return;
    await api.delete(`/packing/${id}`);
    load();
    api.get("/accessories").then((r) => setAccessories(r.data));
  };

  const sendForPacking = (r) => {
    setEditing(null);
    setF({
      ...emptyForm(),
      lines: [{
        product_id: r.product_id,
        pcs: String(r.available_pcs),
        labels_used: String(r.available_pcs),
        stickers_used: String(r.available_pcs),
        boxes_used: String(r.full_boxes),
        stitching_job_id: r.stitching_job_id || "",
      }],
    });
    window.scrollTo({ top: 400, behavior: "smooth" });
  };

  const removeBatch = async (r, label) => {
    if (!window.confirm(`Remove this batch (${label} · ${r.available_pcs} pcs) from Ready-for-Packing?\n\nThis deletes the stitching batch from stock. Use this only for cancelled/write-off batches.`)) return;
    try {
      await api.delete(`/stitching/${r.stitching_job_id}`);
      toast.success("Batch removed from Ready-for-Packing");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail));
    }
  };

  return (
    <div>
      <PageHeader label="Stage 07" title="Packing" subtitle="Ready-for-Packing → Pack · Loose pcs tracked automatically" />
      {ready.length > 0 && (
        <div className="px-4 md:px-8 pt-4" data-testid="packing-ready">
          <Card
            title={`Ready for Packing · ${ready.length} SKU${ready.length === 1 ? "" : "s"}`}
            right={
              <button
                onClick={() => {
                  api.get("/ready-for-packing", { headers: { "Cache-Control": "no-cache" } })
                    .then((r) => {
                      setReady(r.data);
                      toast.success("Refreshed · latest stitched vs packed pulled from server");
                    })
                    .catch(() => toast.error("Refresh failed"));
                }}
                data-testid="packing-ready-refresh"
                className="h-8 px-3 border border-border bg-white hover:bg-secondary font-mono text-[10px] tracking-[0.15em] uppercase font-bold flex items-center gap-2"
                title="Force-refresh (bypass cache)"
              >
                <ArrowClockwise size={12} weight="bold" /> Refresh
              </button>
            }
          >
            <div className="table-scroll hidden md:block">
              <table className="table-dense w-full">
                <thead><tr>
                  <th>Product</th>
                  <th>Batch</th>
                  <th className="text-right">Received</th>
                  <th className="text-right">Packed</th>
                  <th className="text-right">Available</th>
                  <th className="text-right">Full Boxes</th>
                  <th className="text-right">Loose Pcs</th>
                  <th></th>
                  <th></th>
                </tr></thead>
                <tbody>
                  {ready.map((r) => {
                    const label = [r.style_name, r.sku, r.size, r.color].filter(Boolean).join(" / ");
                    const batchTag = [r.batch_date, r.batch_source].filter(Boolean).join(" · ");
                    const rowKey = r.stitching_job_id || r.product_id;
                    return (
                      <tr key={rowKey} data-testid={`packing-ready-${rowKey}`}>
                        <td>{label}</td>
                        <td className="text-xs text-muted-foreground whitespace-nowrap">{batchTag || "—"}</td>
                        <td className="tabular text-right">{r.stitched_pcs}</td>
                        <td className="tabular text-right">{r.packed_pcs}</td>
                        <td className="tabular text-right font-bold text-accent">{r.available_pcs}</td>
                        <td className="tabular text-right">{r.full_boxes}</td>
                        <td className="tabular text-right">{r.loose_pcs}</td>
                        <td className="text-right">
                          <button
                            onClick={() => sendForPacking(r)}
                            data-testid={`send-for-packing-${rowKey}`}
                            className="label-caps text-accent hover:underline"
                          >
                            → Pack
                          </button>
                        </td>
                        <td className="text-right whitespace-nowrap">
                          {isAdmin && r.stitching_job_id && (
                            <button
                              onClick={() => removeBatch(r, label)}
                              data-testid={`remove-from-packing-${rowKey}`}
                              className="label-caps text-destructive hover:underline"
                            >
                              Delete
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {/* Mobile: one card per row — big touch targets, no horizontal scroll. */}
            <div className="md:hidden space-y-3">
              {ready.map((r) => {
                const label = [r.style_name, r.sku, r.size, r.color].filter(Boolean).join(" / ");
                const batchTag = [r.batch_date, r.batch_source].filter(Boolean).join(" · ");
                const rowKey = r.stitching_job_id || r.product_id;
                return (
                  <div key={rowKey} className="border border-border bg-white p-3" data-testid={`packing-ready-card-${rowKey}`}>
                    <div className="font-semibold text-sm">{label}</div>
                    {batchTag && <div className="text-[10px] text-muted-foreground font-mono mt-0.5">{batchTag}</div>}
                    <div className="grid grid-cols-3 gap-2 mt-3">
                      <div>
                        <div className="label-caps text-muted-foreground text-[9px]">Received</div>
                        <div className="tabular font-bold">{r.stitched_pcs}</div>
                      </div>
                      <div>
                        <div className="label-caps text-muted-foreground text-[9px]">Packed</div>
                        <div className="tabular">{r.packed_pcs}</div>
                      </div>
                      <div>
                        <div className="label-caps text-accent text-[9px]">Available</div>
                        <div className="tabular font-bold text-accent">{r.available_pcs}</div>
                      </div>
                      <div>
                        <div className="label-caps text-muted-foreground text-[9px]">Full Boxes</div>
                        <div className="tabular">{r.full_boxes}</div>
                      </div>
                      <div>
                        <div className="label-caps text-muted-foreground text-[9px]">Loose</div>
                        <div className="tabular">{r.loose_pcs}</div>
                      </div>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                      <button
                        onClick={() => sendForPacking(r)}
                        data-testid={`send-for-packing-mobile-${rowKey}`}
                        className="flex-1 h-10 bg-foreground text-background font-mono text-[11px] tracking-[0.15em] uppercase font-bold"
                      >
                        → Pack
                      </button>
                      {isAdmin && r.stitching_job_id && (
                        <button
                          onClick={() => removeBatch(r, label)}
                          data-testid={`remove-from-packing-mobile-${rowKey}`}
                          className="h-10 px-4 border border-destructive text-destructive font-mono text-[11px] tracking-[0.15em] uppercase font-bold"
                        >
                          Delete
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </Card>
        </div>
      )}
      <div className="p-8 grid grid-cols-1 xl:grid-cols-3 gap-4">
        <Card title={editing ? "Edit Packing Entry" : "New Packing Batch"} right={editing && (
          <button type="button" onClick={cancelEdit} className="label-caps hover:text-destructive">Cancel</button>
        )}>
          <form onSubmit={submit} className="space-y-3" data-testid="packing-form">
            <Field label="Date"><Input type="date" required value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} /></Field>
            <Field label="Person packed">
              <Select data-testid="packing-worker" value={f.worker_id} onChange={(e) => setF({ ...f, worker_id: e.target.value })}>
                <option value="">— unassigned —</option>
                {workers.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
              </Select>
            </Field>

            <div className="border border-border p-3 bg-white space-y-2" data-testid="packing-lines">
              <div className="flex items-center justify-between">
                <div className="label-caps">Products</div>
                {!editing && (
                  <button type="button" onClick={addLine} data-testid="packing-add-line" className="label-caps text-accent hover:underline">+ Add Product</button>
                )}
              </div>
              {f.lines.map((l, idx) => {
                const ppb = ppbFor(l.product_id);
                return (
                  <div key={idx} className="border border-dashed border-border p-2 bg-secondary/40 space-y-2" data-testid={`packing-line-${idx}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Line {idx + 1}</span>
                      {!editing && f.lines.length > 1 && (
                        <button type="button" onClick={() => removeLine(idx)} className="label-caps hover:text-destructive" data-testid={`packing-remove-line-${idx}`}>Remove</button>
                      )}
                    </div>
                    <Combobox
                      testid={`packing-product-${idx}`}
                      required
                      value={l.product_id}
                      onChange={(v) => updateLine(idx, { product_id: v })}
                      placeholder="Type to search product / SKU / size…"
                      options={products.map((p) => ({
                        value: p.id,
                        label: `${p.style_name} / ${p.sku} / ${p.size} / ${p.color}`,
                        hint: p.pieces_per_box ? `${p.pieces_per_box} pcs / box` : "",
                      }))}
                    />
                    {l.product_id && ppb > 0 && (
                      <div className="text-[11px] font-mono tabular text-muted-foreground">
                        {ppb} pcs / box · boxes auto-fills from pcs (editable)
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-2">
                      <Field label="Pieces Packed">
                        <Input type="number" min="1" required value={l.pcs} onChange={(e) => updateLine(idx, { pcs: e.target.value })} data-testid={`packing-pcs-${idx}`} />
                      </Field>
                      <Field label="Boxes Used">
                        <Input type="number" value={l.boxes_used} onChange={(e) => updateLine(idx, { boxes_used: e.target.value })} data-testid={`packing-boxes-${idx}`} />
                      </Field>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <Field label={`Labels used (${stockOf("label")} avl)`}>
                        <Input type="number" value={l.labels_used} onChange={(e) => updateLine(idx, { labels_used: e.target.value })} data-testid={`packing-labels-${idx}`} />
                      </Field>
                      <Field label={`Stickers used (${stockOf("sticker")} avl)`}>
                        <Input type="number" value={l.stickers_used} onChange={(e) => updateLine(idx, { stickers_used: e.target.value })} data-testid={`packing-stickers-${idx}`} />
                      </Field>
                    </div>
                  </div>
                );
              })}
              <div className="border-t border-border pt-2 flex items-center justify-between text-sm font-mono tabular" data-testid="packing-totals">
                <span>Totals</span>
                <span><b>{totals.pcs}</b> pcs · <b>{totals.boxes}</b> boxes · {totals.labels} labels · {totals.stickers} stickers</span>
              </div>
            </div>

            <Field label="Notes"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} /></Field>
            <Btn data-testid="packing-submit">{editing ? "Save Changes" : `Save Packing (${f.lines.filter(l => l.product_id && Number(l.pcs) > 0).length} line${f.lines.filter(l => l.product_id && Number(l.pcs) > 0).length === 1 ? "" : "s"})`}</Btn>
          </form>
        </Card>

        <Card title="Packing History" className="xl:col-span-2">
          <div className="table-scroll">
            <table className="table-dense w-full">
              <thead><tr><th>Date</th><th>Product</th><th className="text-right">Pcs</th><th className="text-right">Boxes Packed</th><th className="text-right">Labels</th><th className="text-right">Stickers</th><th>Person</th><th>By</th><th></th></tr></thead>
              <tbody>
                {list.map((p) => (
                  <tr key={p.id} className={editing === p.id ? "bg-secondary" : ""}>
                    <td className="font-mono">{p.date}</td>
                    <td>{p.product_label}</td>
                    <td className="tabular text-right font-bold">{p.pcs}</td>
                    <td className="tabular text-right font-bold">{p.boxes_used}</td>
                    <td className="tabular text-right">{p.labels_used}</td>
                    <td className="tabular text-right">{p.stickers_used}</td>
                    <td>{p.worker_name || <span className="text-muted-foreground">—</span>}</td>
                    <td className="text-xs text-muted-foreground">{p.created_by_name || "—"}</td>
                    <td className="text-right whitespace-nowrap">
                      {isAdmin && (
                        <>
                          <button onClick={() => startEdit(p)} className="label-caps hover:text-foreground mr-3" data-testid={`packing-edit-${p.id}`}>Edit</button>
                          <button onClick={() => del(p.id)} className="label-caps hover:text-destructive">Del</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
                {list.length === 0 && <tr><td colSpan={9}><Empty text="No packing entries yet" /></td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}
